import { getBrowserTimezone, setDisplayTimezone } from '@app/utils/dates/displayTimezone';

type SaveZone = (timezone: string) => Promise<unknown>;

const statusOf = (error: unknown): number | undefined => {
  const e = error as { status?: number; response?: { status?: number } } | null;
  return e?.response?.status ?? e?.status;
};

/**
 * The browser's zone is the truth: render in it, and move users.timezone to it when they differ
 * (a traveller's notifications and exports then follow them).
 * One save at a time; a zone the server refused (4xx) is not retried this session.
 */
export const createProfileTimezoneSync = (save: SaveZone) => {
  let profileZone: string | null | undefined;
  let inFlight: Promise<void> | null = null;
  const refused = new Set<string>();

  const sync = (): Promise<void> => {
    const browserZone = getBrowserTimezone();
    setDisplayTimezone(browserZone);
    if (inFlight) return inFlight;
    if (profileZone === undefined || profileZone === browserZone || refused.has(browserZone)) return Promise.resolve();
    inFlight = save(browserZone)
      .then(() => {
        profileZone = browserZone;
      })
      .catch((error) => {
        const status = statusOf(error);
        if (status !== undefined && status >= 400 && status < 500) refused.add(browserZone);
      })
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  };

  return {
    /** Records users.timezone as loaded, then syncs. */
    loaded: (zone: string | null | undefined) => {
      profileZone = zone ?? null;
      return sync();
    },
    sync,
  };
};
